import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { VariableNodeView } from './VariableNodeView';

export interface VariableOptions {
    /** Sample data to show for variables in readonly mode */
    sampleData?: Record<string, string>;
    /** Whether the editor is read-only */
    readOnly?: boolean;
    /** Called when a variable is deleted */
    onDelete?: (name: string) => void;
    /** Called when a variable is renamed */
    onRename?: (oldName: string, newName: string) => void;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        variable: {
            /**
             * Insert a variable node
             */
            insertVariable: (name: string) => ReturnType;
        };
    }
}

/**
 * TipTap extension for {{variable}} chips
 * 
 * Renders variables as interactive inline nodes with:
 * - Icon + formatted name
 * - Delete button (x)
 * - Click to edit name
 * - Sample data display in readonly mode
 */
export const VariableExtension = Node.create<VariableOptions>({
    name: 'variable',

    group: 'inline',

    inline: true,

    atom: true, // Can't edit inside the node

    addOptions() {
        return {
            sampleData: {},
            readOnly: false,
            onDelete: undefined,
            onRename: undefined,
        };
    },

    addAttributes() {
        return {
            name: {
                default: 'variable',
                parseHTML: (element) => element.getAttribute('data-name'),
                renderHTML: (attributes) => ({
                    'data-name': attributes.name,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="variable"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes({ 'data-type': 'variable' }, HTMLAttributes)];
    },

    addNodeView() {
        return ReactNodeViewRenderer(VariableNodeView);
    },

    addCommands() {
        return {
            insertVariable:
                (name: string) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: { name },
                    });
                },
        };
    },
});

